/* eslint-disable no-use-before-define */
// src/contexts/RoleContext.jsx
import React, { createContext, useContext, useState, useCallback } from 'react';
import secureLocalStorage from 'react-secure-storage';

const RoleContext = createContext();

export const RoleProvider = ({ children }) => {
    // Initialize roles and active role from secure storage
    const [roles, setRoles] = useState(() => {
        try {
            const storedRoles = secureLocalStorage.getItem('user_roles');
            return storedRoles ? JSON.parse(storedRoles) : [];
        } catch (error) {
            console.error('Error loading roles from storage:', error);
            return [];
        }
    });

    const [activeRole, setActiveRole] = useState(() => {
        try {
            return secureLocalStorage.getItem('active_role') || '';
        } catch (error) {
            console.error('Error loading active role from storage:', error);
            return '';
        }
    });

    // Update roles in storage when they change
    const updateRoles = useCallback((newRoles) => {
        try {
            setRoles(newRoles);
            secureLocalStorage.setItem('user_roles', JSON.stringify(newRoles));
        } catch (error) {
            console.error('Error updating roles:', error);
        }
    }, []);

    // Role checking helper functions
    const hasRole = useCallback((roleName) => roles.some((r) => r.role.name === roleName), [roles]);

    const checkRole = useCallback((roleName) => activeRole === roleName, [activeRole]);

    // Role-specific check methods
    const isEmployee = useCallback(() => checkRole('ROLE_EMPLOYEE'), [checkRole]);
    const isSupervisor = useCallback(() => checkRole('ROLE_SUPERVISOR'), [checkRole]);
    const isHOD = useCallback(() => checkRole('ROLE_HOD'), [checkRole]);
    const isFinance = useCallback(() => checkRole('ROLE_FINANCE'), [checkRole]);
    const isAccountant = useCallback(() => checkRole('ROLE_ACCOUNTANT'), [checkRole]);

    // Context value with memoized methods
    const contextValue = {
        roles,
        activeRole,
        setRoles: updateRoles,
        // selectRole,
        hasRole,
        isEmployee,
        isSupervisor,
        isHOD,
        isFinance,
        isAccountant,
        // Add any role-specific permissions
        permissions: {
            canAccessTrips: useCallback(() => ['ROLE_EMPLOYEE', 'ROLE_SUPERVISOR', 'ROLE_HOD'].includes(activeRole), [activeRole]),
            canAccessAdmin: useCallback(() => ['ROLE_SUPERVISOR', 'ROLE_HOD', 'ROLE_FINANCE'].includes(activeRole), [activeRole]),
            canAccessFinance: useCallback(() => ['ROLE_FINANCE', 'ROLE_ACCOUNTANT'].includes(activeRole), [activeRole]),
            canApproveTrips: useCallback(() => ['ROLE_SUPERVISOR', 'ROLE_HOD'].includes(activeRole), [activeRole]),
            canViewReports: useCallback(() => ['ROLE_HOD', 'ROLE_FINANCE'].includes(activeRole), [activeRole])
        }
    };

    return <RoleContext.Provider value={contextValue}>{children}</RoleContext.Provider>;
};

// Custom hook with error handling
export const useRole = () => {
    const context = useContext(RoleContext);
    if (!context) {
        throw new Error('useRole must be used within a RoleProvider');
    }
    return context;
};

// Export types for TypeScript (optional)
export const ROLES = {
    EMPLOYEE: 'ROLE_EMPLOYEE',
    SUPERVISOR: 'ROLE_SUPERVISOR',
    HOD: 'ROLE_HOD',
    FINANCE: 'ROLE_FINANCE',
    ACCOUNTANT: 'ROLE_ACCOUNTANT'
};
